/**
 * AI orchestration — Claude chat, UI state from responses, project-aware payloads.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { generateArchitectReply } from "../services/generateArchitectReply.js";
import { useQuota } from "../context/QuotaContext.jsx";
import { orchestrateExecuteAction } from "../services/executionOrchestrator.js";
import {
  buildClientArchitectFallback,
  shouldGracefulDegradeArchitectError,
} from "../utils/architectGracefulDegrade.js";

/** Aligned with useIdwiz conversation memory shape */
export const ARCHITECT_INITIAL_MEMORY = {
  projectName: null,
  facts: [],
  discoveryAnswers: [],
  proposedConfigDraft: null,
  currentCapabilities: null,
  sswsInterview: { readOnlyTerraformMode: false },
  consultationMode: true,
  agentMode: "CONSULTATION",
  deploymentPhase: null,
  lastContext: null,
  /** Set after successful Okta app create (executeAction) for SP-side / metadata flows */
  lastCreatedOktaApp: null,
};

/**
 * @param {object} opts
 * @param {string} opts.selectedTreePath
 * @param {string} opts.currentContext
 * @param {string[]} [opts.hiddenSystemEvents]
 * @param {object | null} [opts.project]
 * @param {string | null} [opts.apiToken]
 * @param {() => unknown} [opts.getAuditFindings] Live audit findings (auditData) before project.auditSnapshot
 * @param {() => Promise<unknown>} [opts.refreshProject] Reload project after propose (Approvals drawer)
 */
export function useArchitect({
  selectedTreePath,
  currentContext,
  hiddenSystemEvents = [],
  project = null,
  apiToken = null,
  getAuditFindings = null,
  refreshProject = null,
}) {
  const { isSendLocked, triggerRateLimit } = useQuota();

  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [activeOptionButtons, setActiveOptionButtons] = useState([]);
  const [activeFormFields, setActiveFormFields] = useState([]);
  const [primaryActionLabel, setPrimaryActionLabel] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [proposedConfig, setProposedConfig] = useState(null);
  const [warning, setWarning] = useState(null);
  const [bestPracticeViolation, setBestPracticeViolation] = useState(false);
  const [conversationMemory, setConversationMemory] = useState(() => ({ ...ARCHITECT_INITIAL_MEMORY }));
  const [architectureSteps, setArchitectureSteps] = useState([]);
  const [error, setError] = useState(null);
  const [roadmapUpdate, setRoadmapUpdate] = useState(null);
  const [memoryUpdate, setMemoryUpdate] = useState(null);

  const memoryRef = useRef(conversationMemory);
  memoryRef.current = conversationMemory;

  const projectRef = useRef(project);
  projectRef.current = project;

  const getAuditFindingsRef = useRef(getAuditFindings);
  getAuditFindingsRef.current = getAuditFindings;

  const refreshProjectRef = useRef(refreshProject);
  refreshProjectRef.current = refreshProject;

  const sendMessageRef = useRef(
    /** @type {(text: string, opts?: object) => Promise<unknown>} */
    async () => {},
  );

  const mountedRef = useRef(true);
  const abortControllerRef = useRef(/** @type {AbortController | null} */ (null));
  const executionCountRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    executionCountRef.current = 0;
    return () => {
      mountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const updateMemory = useCallback((updates) => {
    setConversationMemory((prev) => {
      const next = { ...prev, ...updates };
      memoryRef.current = next;
      return next;
    });
  }, []);

  const applyResponse = useCallback(
    (response, options = {}) => {
      const { suppressAssistantMessage = false } = options;
      /** Never show Claude's natural-language reply before Okta execution completes — it may claim success prematurely. */
      if (response.message && !suppressAssistantMessage) {
        const raw = response.message;
        const leaked =
          typeof raw === "string" && /\[SYSTEM(?:_ERROR)?:/i.test(raw);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: raw,
            architectureSteps: response.architectureSteps || [],
            timestamp: Date.now(),
            hidden: leaked,
          },
        ]);
      }

      if (response.optionButtons?.length) {
        setActiveOptionButtons(response.optionButtons);
      } else {
        setActiveOptionButtons([]);
      }

      if (response.formFields?.length) {
        setActiveFormFields(response.formFields);
      } else {
        setActiveFormFields([]);
      }

      if (Object.prototype.hasOwnProperty.call(response, "primaryActionLabel")) {
        setPrimaryActionLabel(
          response.primaryActionLabel != null && String(response.primaryActionLabel).trim()
            ? String(response.primaryActionLabel).trim()
            : null,
        );
      }

      if (response.activeTask !== undefined) {
        setActiveTask(response.activeTask);
      }

      if (response.proposedConfig) {
        setProposedConfig(response.proposedConfig);
        updateMemory({ proposedConfigDraft: response.proposedConfig });
      }

      if (response.architectureSteps?.length) {
        setArchitectureSteps(response.architectureSteps);
      }

      if (response.warning) {
        setWarning(response.warning);
        setBestPracticeViolation(response.bestPracticeViolation ?? false);
      } else {
        setWarning(null);
        setBestPracticeViolation(false);
      }

      if (response.revealTenantInputs) {
        updateMemory({ awaitingTenantInput: true });
      }

      if (response.activeTask) {
        updateMemory({ consultationMode: true });
      }

      setRoadmapUpdate(response.roadmapUpdate ?? null);
      setMemoryUpdate(response.memoryUpdate ?? null);
    },
    [updateMemory, setMessages],
  );

  const clearRoadmapUpdate = useCallback(() => setRoadmapUpdate(null), []);
  const clearMemoryUpdate = useCallback(() => setMemoryUpdate(null), []);

  const resolveAuditFindings = useCallback(() => {
    const fromLive =
      typeof getAuditFindingsRef.current === "function" ? getAuditFindingsRef.current() : undefined;
    if (fromLive != null) return fromLive;
    const p = projectRef.current;
    return p?.auditSnapshot?.findings ?? undefined;
  }, []);

  const buildBasePayload = useCallback(
    (extra = {}) => {
      const p = projectRef.current;
      return {
        messages,
        currentContext,
        selectedTreePath,
        conversationMemory: memoryRef.current,
        hiddenSystemEvents,
        project: p ?? undefined,
        auditFindings: resolveAuditFindings(),
        ...extra,
      };
    },
    [messages, currentContext, selectedTreePath, hiddenSystemEvents, resolveAuditFindings],
  );

  const sendMessage = useCallback(
    async (userMessage, opts = {}) => {
      if (!mountedRef.current) return;

      if (import.meta.env.DEV && typeof userMessage === "string") {
        // eslint-disable-next-line no-console
        console.log("[useArchitect] sendMessage called:", userMessage.slice(0, 80));
      }
      if (opts?.rawPayload != null) {
        if (!mountedRef.current) {
          // eslint-disable-next-line no-console
          console.log("[useArchitect] unmounted — stopping execution loop");
          return;
        }
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        return generateArchitectReply({
          ...opts.rawPayload,
          _abortSignal: abortControllerRef.current.signal,
        });
      }
      if (!userMessage?.trim()) return;
      if (isSendLocked) return;
      if (!mountedRef.current) {
        // eslint-disable-next-line no-console
        console.log("[useArchitect] unmounted — stopping execution loop");
        return;
      }

      if (!opts?._isSystemMessage) {
        executionCountRef.current = 0;
      } else {
        executionCountRef.current += 1;
        if (executionCountRef.current > 3) {
          // eslint-disable-next-line no-console
          console.warn("[useArchitect] Execution limit reached — breaking loop");
          executionCountRef.current = 0;
          setIsThinking(false);
          return;
        }
        if (!mountedRef.current) {
          // eslint-disable-next-line no-console
          console.warn("[useArchitect] Component unmounted — stopping");
          return;
        }
      }

      setError(null);
      setIsThinking(true);

      const userMsg = {
        role: "user",
        content: userMessage,
        timestamp: Date.now(),
        hidden: opts?._isSystemMessage === true,
      };
      const visibleForApi = (messages || []).filter((m) => m.hidden !== true);
      const historyForApi = [...visibleForApi, userMsg];

      setMessages((prev) => [...prev, userMsg]);

      try {
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        const p = projectRef.current;
        const payload = {
          ...buildBasePayload(),
          messages: historyForApi,
          userMessage,
          interactionMode: opts.interactionMode ?? "chat",
          phase: opts.phase,
          project: opts.project ?? p ?? undefined,
          auditFindings: opts.auditFindings ?? resolveAuditFindings(),
          driftChanges: opts.driftChanges ?? undefined,
          _abortSignal: signal,
        };

        if (!mountedRef.current) {
          // eslint-disable-next-line no-console
          console.log("[useArchitect] component unmounted during execution");
          return;
        }

        const response = await generateArchitectReply(payload);
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log("🚀 useArchitect: executeAction =", response.executeAction);
          // eslint-disable-next-line no-console
          console.log("[useArchitect] response.executeAction:", response.executeAction);
          // eslint-disable-next-line no-console
          console.log("[useArchitect] response.message:", response.message?.slice(0, 100));
        }
        const exec = response.executeAction?.type ? response.executeAction : null;
        if (!mountedRef.current) {
          // eslint-disable-next-line no-console
          console.log("[useArchitect] component unmounted during execution");
          return;
        }
        applyResponse(response, { suppressAssistantMessage: Boolean(exec) });

        if (exec) {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log("[useArchitect] EXECUTING:", exec.type);
          }
          if (!mountedRef.current) {
            // eslint-disable-next-line no-console
            console.log("[useArchitect] component unmounted during execution");
            return;
          }
          await orchestrateExecuteAction({
            action: exec,
            project: projectRef.current,
            setMessages,
            sendMessage: (text, o) => sendMessageRef.current(text, o),
            onExecutionResult: (action, result, ok) => {
              if (!ok || !result) return;
              const app = result.app;
              if (app?.id) {
                updateMemory({
                  lastCreatedOktaApp: {
                    id: String(app.id),
                    label: app.label != null ? String(app.label) : "",
                    signOnMode: app.signOnMode != null ? String(app.signOnMode) : "",
                  },
                });
              }
            },
            refreshProject: () => refreshProjectRef.current?.(),
          });
        }
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }
        if (err.code === "rate_limit") {
          triggerRateLimit(err.retryAfter ?? 60);
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: err.message ?? "Rate limit reached. Please wait a moment before continuing.",
              isError: true,
              timestamp: Date.now(),
            },
          ]);
        } else if (shouldGracefulDegradeArchitectError(err, opts)) {
          console.warn("[useArchitect] graceful degrade:", err?.code || err?.message);
          applyResponse(buildClientArchitectFallback(opts));
        } else {
          console.error("[useArchitect] sendMessage error:", err);
          setError(err.message ?? "Something went wrong. Please try again.");
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "I encountered an error. Please try your message again.",
              isError: true,
              timestamp: Date.now(),
            },
          ]);
        }
      } finally {
        if (mountedRef.current) {
          setIsThinking(false);
        }
      }
    },
    [isSendLocked, messages, buildBasePayload, applyResponse, triggerRateLimit, updateMemory],
  );

  sendMessageRef.current = sendMessage;

  const sendContextIntro = useCallback(
    async (context, treePathOverride) => {
      if (isSendLocked) return;
      if (!mountedRef.current) {
        // eslint-disable-next-line no-console
        console.log("[useArchitect] unmounted — skipping context intro");
        return;
      }
      setIsThinking(true);
      setActiveFormFields([]);
      setPrimaryActionLabel(null);
      setWarning(null);
      setBestPracticeViolation(false);

      const p = projectRef.current;
      let interactionMode = "context_intro";
      if (p?.conversationSummary?.summary) {
        interactionMode = "resume";
      } else if (p && !p.discoveryComplete && messages.length === 0) {
        interactionMode = "discovery";
      }

      try {
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        const payload = {
          userMessage: `[CONTEXT_SWITCH] User navigated to: ${context}`,
          messages: [],
          currentContext: context,
          selectedTreePath: treePathOverride ?? selectedTreePath,
          conversationMemory: memoryRef.current,
          hiddenSystemEvents,
          interactionMode,
          project: p ?? undefined,
          auditFindings: resolveAuditFindings(),
          _abortSignal: signal,
        };

        if (!mountedRef.current) {
          // eslint-disable-next-line no-console
          console.log("[useArchitect] component unmounted during execution");
          return;
        }

        const response = await generateArchitectReply(payload);
        const exec = response.executeAction?.type ? response.executeAction : null;
        if (!mountedRef.current) {
          // eslint-disable-next-line no-console
          console.log("[useArchitect] component unmounted during execution");
          return;
        }
        applyResponse(response, { suppressAssistantMessage: Boolean(exec) });
        if (exec) {
          if (!mountedRef.current) {
            // eslint-disable-next-line no-console
            console.log("[useArchitect] component unmounted during execution");
            return;
          }
          await orchestrateExecuteAction({
            action: exec,
            project: projectRef.current,
            setMessages,
            sendMessage: (text, o) => sendMessageRef.current(text, o),
            onExecutionResult: (action, result, ok) => {
              if (!ok || !result) return;
              const app = result.app;
              if (app?.id) {
                updateMemory({
                  lastCreatedOktaApp: {
                    id: String(app.id),
                    label: app.label != null ? String(app.label) : "",
                    signOnMode: app.signOnMode != null ? String(app.signOnMode) : "",
                  },
                });
              }
            },
            refreshProject: () => refreshProjectRef.current?.(),
          });
        }
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }
        if (err.code === "rate_limit") {
          triggerRateLimit(err.retryAfter ?? 60);
        } else {
          console.error("[useArchitect] sendContextIntro error:", err);
        }
      } finally {
        if (mountedRef.current) {
          setIsThinking(false);
        }
      }
    },
    [
      isSendLocked,
      selectedTreePath,
      hiddenSystemEvents,
      applyResponse,
      triggerRateLimit,
      messages.length,
      updateMemory,
      resolveAuditFindings,
    ],
  );

  const resetConversation = useCallback(() => {
    setMessages([]);
    setActiveOptionButtons([]);
    setActiveFormFields([]);
    setActiveTask(null);
    setProposedConfig(null);
    setWarning(null);
    setBestPracticeViolation(false);
    setArchitectureSteps([]);
    setConversationMemory({ ...ARCHITECT_INITIAL_MEMORY });
    setError(null);
    setRoadmapUpdate(null);
    setMemoryUpdate(null);
  }, []);

  const dismissWarning = useCallback(() => {
    setWarning(null);
    setBestPracticeViolation(false);
  }, []);

  return {
    messages,
    isThinking,
    activeOptionButtons,
    activeFormFields,
    activeTask,
    proposedConfig,
    warning,
    bestPracticeViolation,
    conversationMemory,
    architectureSteps,
    error,
    roadmapUpdate,
    memoryUpdate,

    setMessages,
    setIsThinking,
    setActiveOptionButtons,
    setActiveFormFields,
    setPrimaryActionLabel,
    setActiveTask,
    setProposedConfig,
    setWarning,
    setBestPracticeViolation,
    setConversationMemory,
    setArchitectureSteps,

    sendMessage,
    sendContextIntro,
    resetConversation,
    updateMemory,
    dismissWarning,
    clearRoadmapUpdate,
    clearMemoryUpdate,
  };
}
